import { h, inject, resolveComponent, ref, toRaw, reactive, watchEffect } from 'vue';
import { OptionsComponent, fetchPage, getServerItem, fetchPageBuilder } from '@drapejs/core';
import consola from 'consola';
import { defineReceiver, defineCommand } from '@drapejs/invoker';
import fetch from 'cross-fetch';
import merge from 'lodash/merge';

var script$2 = {
  extends: OptionsComponent,
  props: {
    block: {
      type: Object,
      default: null
    },
    class: {
      type: [Object, String],
      default: null
    }
  },
  computed: {
    itemClass() {
      return [this.class, 'storyblok__outline'];
    },

    style() {
      const style = {};

      if (this.block.marginTop) {
        style.marginTop = this.block.marginTop;
      }

      if (this.block.marginBottom) {
        style.marginBottom = this.block.marginBottom;
      }

      return style;
    },

    componentKey() {
      return `block-${this.block.component.replace(/.([A-Z])/g, m => `${m[0]}-${m[1]}`)}`.toLowerCase();
    },

    blockC() {
      var _this$block$_editable;

      return (_this$block$_editable = this.block._editable) === null || _this$block$_editable === void 0 ? void 0 : _this$block$_editable.replace('-->', '').replace('<!--#storyblok#', '');
    },

    blockUid() {
      return `${this.$page._id}-${this.block._uid}`;
    }

  },
  template: '<section :style="style" :data-blok-c="blockC" :data-blok-uid="blockUid" :class="itemClass" v-if="block"><suspense><component :is="componentKey" :block="block" /></suspense></section>'
};

var script$1 = {
  props: {
    blocks: {
      type: Object,
      default: null
    },
    class: {
      type: [Object, String],
      default: null
    }
  },
  computed: {
    itemClass() {
      return this.class || '';
    }

  },
  template: '<block-container v-for="block in blocks" :key="block._uid" :class="itemClass" :block="block"/>'
};

/** Gets datasource entries from cache (if they are fetched) */
const getDataSourceKey = Symbol('getDataSource');
/**
 * If the page originates from an SSR request, this function allows blocking synchronous access to that initial server-side cache state. Useful for rehydrating components in the client since all of Vue's lifecycle hooks ar synchronous.
 *
 * Note that this does not update when the cache is updated during runtime. Use getDataSourceKey instead to get the Cache's current value.
 */

const getServerDataSourceKey = Symbol('getServerDataSource');
/** Subscribe to changes in local cached data source entries (requires fetchDataSourceKey to be called) */

const subscribeDataSourceKey = Symbol('subscribeDataSource');
/** Unsubscribe from changes in local cached data source entries */

const unsubscribeDataSourceKey = Symbol('unsubscribeDataSource');
/** Fetches a data source from Storyblok. Use getDataSourceKey to fetch cached entries */

const fetchDataSourceKey = Symbol('fetchDataSource');
/** Turns a Storyblok link field value into a url and name */

const resolveLinkKey = Symbol('resolveLink');
/** Call function to apply an app-wide rewrite of links resolved by `resolveLinkKey` */

const configureResolveLinkRewriterKey = Symbol('resolveLinkRewriter');
/** Configure a resolver to lookup which language to use for example when resolving links using `resolveLinkKey` */

const configureLanguageResolverKey = Symbol('configureLanguageResolver');
const isInEditorKey = Symbol('isInEditor');

const schema = {
  doc(_node, children) {
    return h('div', children());
  },

  paragraph(_node, children) {
    return h('p', children());
  },

  text(node, _children) {
    let i = -1;

    function nextMark() {
      if (node.marks && Array.isArray(node.marks) && node.marks.length > i) {
        i += 1;
        return node.marks[i];
      }
    }

    function renderMark(mark) {
      if (mark) {
        const map = schema[mark.type];

        if (map) {
          return map(mark, () => renderMark(nextMark()));
        }

        consola.error(`RichText: Could not find schema for type '${mark.type}'`, mark);
      }

      return node.text;
    }

    return renderMark(nextMark());
  },

  styled(node, children) {
    return h('span', node.attrs, children());
  },

  link(node, children) {
    const {
      href,
      target,
      anchor,
      linktype = 'url',
      uuid
    } = node.attrs;

    if (linktype === 'story') {
      const resolveLink = inject(resolveLinkKey);

      if (!resolveLink) {
        return h('router-link');
      }

      const resolved = resolveLink(() => {
        return {
          url: '',
          cached_url: '',
          linktype: '',
          id: uuid
        };
      });
      return h(resolveComponent('router-link'), {
        to: anchor ? `${resolved.url}#${anchor}` : resolved.url,
        target: target
      }, () => children());
    } else if (linktype === 'email') {
      return h('a', {
        href: `mailto:${href}`
      }, children());
    } else {
      return h('a', {
        href,
        target
      }, children());
    }
  },

  horizontal_rule(_node, _children) {
    return h('hr');
  },

  blockquote(_node, children) {
    return h('blockquote', children());
  },

  code_block(node, children) {
    return h('pre', node.attrs, children());
  },

  hard_break(_node, _children) {
    return h('br');
  },

  heading(node, children) {
    return h(`h${node.attrs.level}`, children());
  },

  bullet_list(_node, children) {
    return h('ul', children());
  },

  ordered_list(_node, children) {
    return h('ol', children());
  },

  list_item(_node, children) {
    return h('li', children());
  },

  bold(_node, children) {
    return h('b', children());
  },

  strike(_node, children) {
    return h('strike', children());
  },

  underline(_node, children) {
    return h('u', children());
  },

  strong(_node, children) {
    return h('strong', children());
  },

  code(_node, children) {
    return h('code', children());
  },

  italic(_node, children) {
    return h('i', children());
  },

  image(node, _children) {
    return h('img', {
      src: node.attrs.src,
      alt: node.attrs.alt,
      title: node.attrs.title
    });
  },

  blok(node, _children) {
    return h(resolveComponent('blocks-repeater'), {
      blocks: node.attrs.body
    });
  }

};
var schema$1 = schema;

var script = {
  props: {
    value: {
      type: Object,
      default: null
    }
  },

  setup(props) {
    return () => renderNode(props.value);

    function renderNode(node) {
      if (!node) {
        return;
      }

      const map = schema$1[node.type];

      if (map) {
        return map(node, () => {
          var _node$content;

          return (_node$content = node.content) === null || _node$content === void 0 ? void 0 : _node$content.map(i => renderNode(i));
        });
      }

      consola.error(`RichText: Could not find schema for type '${node.type}'`, node);
    }
  }

};

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, basedir, module) {
	return module = {
	  path: basedir,
	  exports: {},
	  require: function (path, base) {
      return commonjsRequire(path, (base === undefined || base === null) ? module.path : base);
    }
	}, fn(module, module.exports), module.exports;
}

function commonjsRequire () {
	throw new Error('Dynamic requires are not currently supported by @rollup/plugin-commonjs');
}

var lib = createCommonjsModule(function (module, exports) {

  Object.defineProperty(exports, "__esModule", {
    value: true
  });
  exports.postMessage = exports.isNode = exports.isWorker = exports.isBrowser = void 0;

  function isBrowser() {
    return typeof self !== 'undefined' && self.document !== undefined;
  }

  exports.isBrowser = isBrowser;

  function isWorker() {
    return typeof self !== 'undefined' && self.document === undefined;
  }

  exports.isWorker = isWorker;

  function isNode() {
    return !isWorker() && !isBrowser() && typeof commonjsGlobal === 'object';
  }

  exports.isNode = isNode;

  var _self = typeof self === 'undefined' && typeof commonjsGlobal === 'object' ? commonjsGlobal.self : self;

  function postMessage(message, options) {
    return _self.postMessage(message, options);
  }

  exports.postMessage = postMessage;
});
var lib$1 = lib;

function toQueryString (queryParams) {
  const queryItems = [];

  if (queryParams) {
    for (const key of Object.keys(queryParams).sort()) {
      const value = queryParams[key];

      if (value) {
        queryItems.push(`${key}=${value}`);
      } else {
        queryItems.push(`${key}`);
      }
    }
  }

  return queryItems.length > 0 ? `?${queryItems.join('&')}` : '';
}

const cvCacheKey = '__sb_cv';
let cv$1 = undefined;

let pathResolver = command => {
  let path = command.path || '/';

  if (!path || path === '/') {
    path = '/home';
  }

  return path;
};

let queryParamsResolver = _command => {
  return {};
};

const setPathResolver = resolver => {
  if (resolver) {
    const base = pathResolver;

    pathResolver = command => resolver(command, base);
  }
};
const setQueryParamsResolver = resolver => {
  if (resolver) {
    queryParamsResolver = resolver;
  }
};
const fetchPageReceiver = defineReceiver(fetchPage, async function (command, data) {
  const config = await this.cache.getItem('__storyblok');
  const fetchDraft = config.fetchDraft;
  const cached = await this.cache.getItem(command.cacheKey);

  if (!fetchDraft && cached && cv$1 === cached.__sb_cv) {
    // No point in refetching the same data from Storyblok if we already have it in cache
    return cached;
  }

  const queryParams = queryParamsResolver(command) || {};

  if (!lib$1.isNode() && !fetchDraft) {
    await setCacheVersion.call(this, queryParams);
  }

  if (command.findByUuid) {
    queryParams['find_by'] = 'uuid';
  }

  queryParams['token'] = config.token;
  queryParams['version'] = fetchDraft ? 'draft' : 'published';

  if (!config.linkResolution || config.linkResolution === 'url') {
    queryParams['resolve_links'] = 'url';
  }

  var response = await fetch(`//api.storyblok.com/v2/cdn/stories/${pathResolver(command).substring(1)}${toQueryString(queryParams)}`, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  });

  if (response.ok) {
    const body = await response.json();
    cv$1 = body.cv;

    if (lib$1.isNode()) {
      await this.cache.setItem(cvCacheKey, cv$1);
    }

    if (config.linkResolution === 'prefetch') {
      await validateLinks.call(this, command, config.token, fetchDraft);
    }

    const story = body.story;

    if (body.links) {
      await storeLinks.call(this, body.links, story.lang);
    }

    data = translateStoryToCache(story, body.links, body.rels, fetchDraft);
  } else {
    data = {
      http_status: response.status
    };
  }

  this.cache.setItem(command.cacheKey, data);
  return data;
});
const fetchDataSource = defineCommand("fetchDataSource");
const fetchDataSourceReceiver = defineReceiver(fetchDataSource, async function (command, _) {
  const config = await this.cache.getItem('__storyblok');
  const queryParams = {}; // Even though there's no such thing as a draft datasource, we use the same flag to bypass Storyblok's CDN cache.

  if (!lib$1.isNode() && !config.fetchDraft) {
    await setCacheVersion.call(this, queryParams);
  } else {
    // For some reason, Storyblok seems to cache data sources by default, so we always add a unique cv flag to bypass it during development/preview
    queryParams['cv'] = Math.floor(Date.now()).toString();
  }

  if (command.dimension) {
    queryParams['dimension'] = command.dimension;
  }

  queryParams['datasource'] = command.id;
  queryParams['token'] = config.token;
  queryParams['page'] = '1';
  queryParams['per_page'] = '1000';
  var response = await fetch(`//api.storyblok.com/v2/cdn/datasource_entries${toQueryString(queryParams)}`, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  });
  const cacheKey = `__datasource_${command.id}`;
  let data = (await this.cache.getItem(cacheKey)) || {};

  if (response.ok) {
    const body = await response.json();
    body.datasource_entries.forEach(r => {
      if (command.dimension && r.dimension_value) {
        data[r.name] = r.dimension_value;
      } else {
        data[r.name] = r.value;
      }
    });
    await this.cache.setItem(cacheKey, data);
  }

  return data;
});
const fetchStories = defineCommand('fetchStories');
const fetchStoriesReceiver = defineReceiver(fetchStories, async function (command, _) {
  const config = await this.cache.getItem('__storyblok');
  const fetchDraft = config.fetchDraft;
  let query = '';
  const queryItems = [];

  if (command.starts_with) {
    queryItems.push(`starts_with=${command.starts_with}`);
  }

  if (command.is_startpage !== undefined) {
    queryItems.push(`is_startpage=${command.is_startpage ? '1' : '0'}`);
  }

  if (command.sort_by !== undefined) {
    queryItems.push(`sort_by=${command.sort_by}`);
  }

  if (queryItems.length > 0) {
    query = `&${queryItems.join('&')}`;
  }

  let cv = '';

  if (!fetchDraft) {
    cv = '&cv=' + Math.floor(Date.now() / 60000);
  }

  var response = await fetch(`//api.storyblok.com/v2/cdn/stories?token=${config.token}${cv}${query}&version=${fetchDraft ? 'draft' : 'published'}`, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  });

  if (response.ok) {
    const body = await response.json();
    const stories = [];
    const links = [];
    body.stories.forEach(story => {
      const entry = translateStoryToCache(story, body.links, body.rels, fetchDraft);
      this.cache.setItem('/' + story.full_slug, entry);
      stories.push(entry);
      links.push({
        full_slug: story.full_slug,
        id: story.id,
        name: story.name,
        slug: story.slug,
        uuid: story.uuid
      });
    }); // Only default language supported for now

    await storeLinks.call(this, links, 'default');
    return stories;
  }
});

function translateStoryToCache(story, links, rels, fetchDraft) {
  var _story$translated_slu, _story$alternates;

  const data = story.content;
  data.links = links || [];
  data.__sb_rels = (rels || []).reduce((res, item) => {
    res[item.uuid] = item;
    return res;
  }, {});
  data.__sb_cv = cv$1;
  data.__sb_lang = story.lang;
  data.__sb_tag_list = story.tag_list;
  data.__sb_first_published_at = story.first_published_at;
  data.__sb_full_slug = story.full_slug;
  data._uid = story.uuid;
  data.http_status = 200;
  data.alternates = [];

  if (((_story$translated_slu = story.translated_slugs) === null || _story$translated_slu === void 0 ? void 0 : _story$translated_slu.length) > 0) {
    const slug = story.translated_slugs.find(r => r.lang === story.lang);
    data.name = (slug === null || slug === void 0 ? void 0 : slug.name) || story.name;

    if (story.lang === 'default') {
      data.alternates = story.translated_slugs;
    } else {
      data.alternates = [{
        lang: 'default',
        name: story.name,
        path: story.default_full_slug
      }].concat(story.translated_slugs);
    }
  } else {
    data.name = story.name;
  }

  if (((_story$alternates = story.alternates) === null || _story$alternates === void 0 ? void 0 : _story$alternates.length) > 0) {
    data.alternates = data.alternates.concat(story.alternates.filter(s => {
      return fetchDraft || s.published;
    }).map(s => {
      return {
        path: s.full_slug,
        name: s.name
      };
    }));
  }

  data.type = data.component;
  data._id = story.id;
  return data;
}

async function setCacheVersion(queryParams) {
  const cached = await this.cache.getItem(cvCacheKey);

  if (cached) {
    cv$1 = cached; // We only use the cache to pass cache version from SSR to client. But we
    // don't want to keep it in cache indefinitely, as it should refresh on a
    // page refresh.

    await this.cache.setItem(cvCacheKey, undefined);
  }

  if (cv$1) {
    queryParams['cv'] = cv$1;
  }
}

async function validateLinks(command, token, fetchDraft) {
  const linksVersion = await this.cache.getItem('__sb_links_cv');

  if (cv$1 != linksVersion) {
    const queryParams = queryParamsResolver(command) || {};

    if (!fetchDraft) {
      await setCacheVersion.call(this, queryParams);
    }

    queryParams['token'] = token;
    queryParams['version'] = fetchDraft ? 'draft' : 'published';
    var response = await fetch(`//api.storyblok.com/v2/cdn/links/${toQueryString(queryParams)}`, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      await this.cache.setItem('__sb_links_cv', cv$1);
      const body = await response.json();
      const links = {};
      Object.values(body.links).forEach(link => {
        let translations = {
          default: {
            url: stripTrailingSlash(link.slug),
            name: link.name
          }
        };

        if (link.alternates) {
          translations = link.alternates.reduce((res, alternative) => {
            res[alternative.lang] = {
              url: stripTrailingSlash(alternative.path),
              name: alternative.name
            };
            return res;
          }, translations);
        }

        links[link.uuid] = translations;
      }, {});
      await this.cache.setItem('__sb_links', links);
    }
  }
}

async function storeLinks(links, language) {
  let cachedLinks = await this.cache.getItem('__sb_links');

  if (!cachedLinks) {
    cachedLinks = {};
  }

  links.forEach(l => {
    // For some reason, Storyblok returns Content Delivery API v1 style links, so we need to remove the language prefix
    let slug = l.full_slug;

    if (slug.indexOf(language) === 0) {
      slug = slug.substring(language.length + 1);
    }

    if (slug.indexOf('/') !== 0) {
      slug = `/${slug}`;
    }

    const val = {
      url: stripTrailingSlash(slug),
      name: l.name
    };

    if (!cachedLinks[l.uuid]) {
      cachedLinks[l.uuid] = {};
    }

    cachedLinks[l.uuid][language] = val;
  });
  await this.cache.setItem('__sb_links', cachedLinks);
}

function stripTrailingSlash(url) {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

class LinkResolver {
  constructor(cache) {
    _defineProperty(this, "links", ref(undefined));

    this.cache = cache;
    this.links.value = getServerItem('__sb_links');

    if (!this.links.value) {
      this.cache.getItem('__sb_links').then(val => {
        this.links.value = val || {};
      });
    }

    this.cache.subscribe('__sb_links', val => {
      this.links.value = val || {};
    });
  }

  resolveLink(link, lang = 'default') {
    var _this$links$value;

    if (!link) return {
      url: '',
      name: '',
      isExternal: false
    };

    if (link.linktype === 'email') {
      return {
        url: 'mailto:' + link.email,
        name: link.email,
        isExternal: true
      };
    }

    if (link.url) {
      return {
        url: link.url,
        name: link.url,
        isExternal: true
      };
    }

    if (!link.id) {
      return {
        url: '',
        name: '',
        isExternal: false
      };
    }

    const match = (_this$links$value = this.links.value) === null || _this$links$value === void 0 ? void 0 : _this$links$value[link.id];

    if (match) {
      return { ...match[lang],
        isExternal: false
      };
    }

    consola.warn(`Unable to resolve link for UUID '${link.id}' - Try refresh the page`, link);
    return {
      url: '',
      name: '',
      isExternal: false
    };
  }

}
/**
 * Resolved the link for the given story. If link is not cached, it will be fetched from Storyblok.
 * @param token
 * @param id
 */

function getLinkForStory(_token, _id, _invoker) {}

function setupEditor (app, options) {
  if (typeof window !== 'object' || window.location.href.indexOf('_storyblok=') < 0) {
    return;
  }

  const isInEditor = ref(false);
  app.provide(isInEditorKey, isInEditor);
  loadStoryblokBridge().then(() => {
    connectEditor(app, options);
  });

  function loadStoryblokBridge() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = '//app.storyblok.com/f/storyblok-v2-latest.js';
      const el = document.getElementsByTagName('head')[0];
      el.appendChild(script);
      script.addEventListener('load', () => {
        resolve(script);
      });
      script.addEventListener('error', () => {
        reject();
        consola.error('Storyblok Bridge failed to load');
      });
    });
  }

  function connectEditor(app, options) {
    const storyblokInstance = new window.StoryblokBridge({
      preventClicks: true
    });
    storyblokInstance.pingEditor(async () => {
      if (storyblokInstance.isInEditor()) {
        storyblokInstance.enterEditmode();
        isInEditor.value = true;
        app.disableNavigate();
        await app.cache.setItem('__storyblok', {
          token: options.token,
          fetchDraft: true
        });
        var command = fetchPageBuilder(window.location.protocol, window.location.host, window.location.pathname); // Clear current state to trigger a re-render so that Storyblok picks up the change and hooks up the editor

        var current = await app.cache.getItem(command.cacheKey);
        await app.cache.setItem(command.cacheKey, {
          type: current.type,
          status: current.status
        });
        await app.invoker.invoke(fetchPage, fetchPageBuilder(window.location.protocol, window.location.host, window.location.pathname));
      }
    });
    storyblokInstance.on(['change', 'published'], event => {
      if ((event === null || event === void 0 ? void 0 : event.action) === 'change' || (event === null || event === void 0 ? void 0 : event.action) === 'published') {
        app.invoker.invoke(fetchPage, fetchPageBuilder(window.location.protocol, window.location.host, '/' + event.slug));
      }
    });
    storyblokInstance.on('input', async function (event) {
      if (event.action === 'input') {
        let current = await app.cache.getItem(window.location.pathname);

        if (!current) {
          current = event.story.content;
        } else {
          merge(current, event.story.content);
        }

        current.name = event.name;
        app.cache.setItem(window.location.pathname, current);
      }
    });
  }
}

let cv = '';
let redirects = undefined;
const serverRedirects = defineReceiver(fetchPage, async function (command, data) {
  if (!lib$1.isNode()) {
    return;
  }

  await validateRedirects.call(this, data);
  const isRedirect = typeof command.query['_sbr'] !== 'undefined';

  if (data.http_status !== 404 || !redirects || isRedirect) {
    return;
  }

  delete command.query['_sbr'];
  const original = `${command.protocol}//${command.host}${command.path}${toQueryString(command.query)}`;
  let target = original;

  for (const redirect of redirects) {
    target = target.replace(redirect.pattern, redirect.location);
  }

  if (original !== target) {
    target += (target.indexOf('?') < 0 ? '?' : '&') + `_sbr`;
    data.http_location = target;
    data.http_status = 301;
    consola.info(`Storyblok: Redirecting to ${target}`);
    this.cache.setItem(command.cacheKey, data);
    this.break();
  }
});

async function validateRedirects(data) {
  if (typeof redirects === 'undefined' || data.__sb_cv !== cv) {
    consola.info('Storyblok: Refreshing server-side redirects');
    const config = await this.cache.getItem('__storyblok');
    const queryParams = {}; // For some reason, Storyblok seems to cache data sources by default, so we always add a unique cv flag to bypass it during development/preview

    queryParams['cv'] = Math.floor(Date.now()).toString();
    queryParams['datasource'] = 'redirects';
    queryParams['token'] = config.token;
    queryParams['page'] = '1';
    queryParams['per_page'] = '1000';
    var response = await fetch(`https://api.storyblok.com/v2/cdn/datasource_entries${toQueryString(queryParams)}`, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const body = await response.json(); // It's possible that a different promise has already updated the redirects

      if (data.__sb_cv && data.__sb_cv === cv) {
        return;
      }

      redirects = body.datasource_entries.map(r => {
        return {
          pattern: new RegExp(r.name),
          location: r.value
        };
      });

      if (data.__sb_cv) {
        cv = data.__sb_cv;
      }
    } else {
      consola.error('Storyblok: Failed to update server-side redirects', JSON.stringify(await response.json()));
    }
  }
}

/**
 * Resizes or crops an image to be contained within the given dimensions, using the image pointer's focus point if available.
 *
 * @param image is an object returned by Storyblok Content API when you have an image reference
 */

function imageResize(image, dimensions) {
  if (!image || !dimensions || !image.filename || image.filename.endsWith('.svg')) {
    return image;
  }

  let origDimensions = {
    width: image.filename.split('/')[5].split('x')[0],
    height: image.filename.split('/')[5].split('x')[1]
  };

  if (typeof window === 'object' && window.devicePixelRatio) {
    dimensions.width = dimensions.width && Math.ceil(dimensions.width * window.devicePixelRatio);
    dimensions.height = dimensions.height && Math.ceil(dimensions.height * window.devicePixelRatio);
  }

  let params = `/${dimensions.width || 0}x${dimensions.height || 0}`;
  const filters = []; // iOS 13 seems to have issues is image is a proxy, so we ensure it's a raw object

  const result = { ...toRaw(image)
  };

  if (dimensions.width && dimensions.height) {
    result.width = dimensions.width;
    result.height = dimensions.height;
  } else {
    // Calculate the not-specified side
    const origAspect = origDimensions.width / origDimensions.height;
    const boundingAspect = (dimensions.width || Number.MAX_SAFE_INTEGER) / (dimensions.height || Number.MAX_SAFE_INTEGER);

    if (boundingAspect < origAspect) {
      // Bounding box is taller than original (proportionally)
      const width = dimensions.width || origDimensions.width;
      result.width = Math.round(width);
      result.height = Math.round(width * (origDimensions.height / origDimensions.width));
    } else if (boundingAspect > origAspect) {
      // Bounding box is equal to or wider than original (proportionally)
      const height = dimensions.height || origDimensions.height;
      result.width = Math.round(height * (origDimensions.width / origDimensions.height));
      result.height = Math.round(height);
    } else {
      result.width = dimensions.width || origDimensions.width;
      result.height = dimensions.height || origDimensions.height;
    }
  }

  if (result.focus) {
    filters.push(`focal(${result.focus})`);
    const [focalX, focalY] = result.focus.split(':')[0].split('x');
    result.focusParsed = {
      x: `${focalX / parseInt(origDimensions.width) * 100}%`,
      y: `${focalY / parseInt(origDimensions.height) * 100}%`
    };
  }

  if (filters.length > 0) {
    params = `${params}/filters:${filters.join(':')}`;
  }

  result.filename = `${result.filename}/m${params}`;
  return result;
}
/**
 * Resizes an image to fit in the given dimensions.
 *
 * @param image is an object returned by Storyblok Content API when you have an image reference
 */

function imageFit(image, dimensions, fill = '') {
  if (!image || !dimensions || !image.filename || image.filename.endsWith('.svg')) {
    return image;
  }

  let origDimensions = {
    width: image.filename.split('/')[5].split('x')[0],
    height: image.filename.split('/')[5].split('x')[1]
  };

  if (typeof window === 'object' && window.devicePixelRatio) {
    dimensions.width = dimensions.width && Math.ceil(dimensions.width * window.devicePixelRatio);
    dimensions.height = dimensions.height && Math.ceil(dimensions.height * window.devicePixelRatio);
  }

  let params = `/${dimensions.width || 0}x${dimensions.height || 0}`;
  const filters = []; // iOS 13 seems to have issues is image is a proxy, so we ensure it's a raw object

  const result = { ...toRaw(image)
  };
  result.width = dimensions.width;
  result.height = dimensions.height;

  if (fill) {
    filters.push(`fill(${fill})`);
  }

  if (result.focus) {
    filters.push(`focal(${result.focus})`);
    const [focalX, focalY] = result.focus.split(':')[0].split('x');
    result.focusParsed = {
      x: `${focalX / parseInt(origDimensions.width) * 100}%`,
      y: `${focalY / parseInt(origDimensions.height) * 100}%`
    };
  }

  if (filters.length > 0) {
    params = `${params}/filters:${filters.join(':')}`;
  }

  result.filename = `${result.filename}/m/fit-in${params}`;
  return result;
}

function useDataSources () {
  return {
    /** Fetches a data source from Storyblok. Use getDataSourceKey to fetch cached entries. */
    fetchDataSource: inject(fetchDataSourceKey, async () => {
      consola.warn('`fetchDataSource` was called but was not provided by the app. Verify you have installed drapejs-storyblok plugin.');
      return {};
    }),

    /** Gets datasource entries from cache (if they are fetched) */
    getDataSource: inject(getDataSourceKey, async () => {
      consola.warn('`getDataSource` was called but was not provided by the app. Verify you have installed drapejs-storyblok plugin.');
      return {};
    }),

    /**
     * If the page originates from an SSR request, this function allows blocking synchronous access to that initial server-side cache state. Useful for rehydrating components in the client since all of Vue's lifecycle hooks ar synchronous.
     *
     * Note that this does not update when the cache is updated during runtime. Use getDataSourceKey instead to get the Cache's current value.
     */
    getServerDataSource: inject(getServerDataSourceKey, () => {
      consola.warn('`getServerDataSourceKey` was called but was not provided by the app. Verify you have installed drapejs-storyblok plugin.');
      return {};
    }),

    /** Subscribe to changes in local cached data source entries (requires fetchDataSourceKey to be called) */
    subscribeDataSource: inject(subscribeDataSourceKey, async () => {
      consola.warn('`subscribeDataSource` was called but was not provided by the app. Verify you have installed drapejs-storyblok plugin.');
    }),

    /** Unsubscribe from changes in local cached data source entries */
    unsubscribeDataSourceKey: inject(unsubscribeDataSourceKey, async () => {
      consola.warn('`unsubscribeDataSourceKey` was called but was not provided by the app. Verify you have installed drapejs-storyblok plugin.');
    })
  };
}

const linkResolution = {
  /**
   * Loads all links from Storyblok and caches them. This requires the least amount of data
   * over the course of a session, but might lead to slow initial load if there are a lot
   * of links
   */
  prefetch: 'prefetch',

  /**
   * Lets Storyblok resolve links in each story fetch. This adds some data to every page
   * request. This is the default behavior.
   *
   * If there are a lot of links on a page, Storyblok will not resolve them all in the
   * initial request, which will cause multiple subsequent requests to be made.
   */
  url: 'url'
}; // install function executed by Vue.use()

const install = function installDrapejsStoryblok(app, options) {
  var _window$__DRAPEJS_STA, _window$__DRAPEJS_STA2, _window$__DRAPEJS_STA3;

  if (typeof window === 'object' && (_window$__DRAPEJS_STA = window.__DRAPEJS_STATE__) !== null && _window$__DRAPEJS_STA !== void 0 && (_window$__DRAPEJS_STA2 = _window$__DRAPEJS_STA.cache) !== null && _window$__DRAPEJS_STA2 !== void 0 && (_window$__DRAPEJS_STA3 = _window$__DRAPEJS_STA2.__storyblok) !== null && _window$__DRAPEJS_STA3 !== void 0 && _window$__DRAPEJS_STA3.token) {
    var _window$__DRAPEJS_STA4, _window$__DRAPEJS_STA5, _window$__DRAPEJS_STA6;

    consola.trace('drapeJS-storyblok: Found token from embedded SSR state, using that instead of supplied token');
    options.token = (_window$__DRAPEJS_STA4 = window.__DRAPEJS_STATE__) === null || _window$__DRAPEJS_STA4 === void 0 ? void 0 : (_window$__DRAPEJS_STA5 = _window$__DRAPEJS_STA4.cache) === null || _window$__DRAPEJS_STA5 === void 0 ? void 0 : (_window$__DRAPEJS_STA6 = _window$__DRAPEJS_STA5.__storyblok) === null || _window$__DRAPEJS_STA6 === void 0 ? void 0 : _window$__DRAPEJS_STA6.token;
  }

  if (!options.token) {
    throw "No Storyblok token set, make sure you set token to .token property";
  }

  if (options.blockComponents) {
    for (const [key, value] of Object.entries(options.blockComponents)) {
      const fullKey = `block-${key.replace(/.([A-Z])/g, m => `${m[0]}-${m[1]}`)}`.toLowerCase();
      app.component(fullKey, value);
    }
  }

  app.cache.setItem('__storyblok', {
    token: options.token,
    fetchDraft: options.fetchDraft || false,
    linkResolution: options.linkResolution || linkResolution.url
  });
  app.provide(fetchDataSourceKey, async (id, dimension) => {
    return await app.invoker.invoke(fetchDataSource, {
      id,
      dimension
    });
  });
  app.provide(getDataSourceKey, async id => {
    return await app.cache.getItem(`__datasource_${id}`);
  });
  app.provide(getServerDataSourceKey, id => {
    var _window$__DRAPEJS_STA7, _window$__DRAPEJS_STA8;

    if (typeof window === 'undefined') {
      return;
    }

    return (_window$__DRAPEJS_STA7 = window.__DRAPEJS_STATE__) === null || _window$__DRAPEJS_STA7 === void 0 ? void 0 : (_window$__DRAPEJS_STA8 = _window$__DRAPEJS_STA7.cache) === null || _window$__DRAPEJS_STA8 === void 0 ? void 0 : _window$__DRAPEJS_STA8[`__datasource_${id}`];
  });
  app.provide(subscribeDataSourceKey, async (id, callback) => {
    await app.cache.subscribe(`__datasource_${id}`, callback);
  });
  app.provide(unsubscribeDataSourceKey, async (id, callback) => {
    await app.cache.unsubscribe(`__datasource_${id}`, callback);
  });
  const linkResolver = new LinkResolver(app.cache);

  let linkRewriter = link => link;

  let langResolver = () => 'default';

  app.provide(resolveLinkKey, (link, lang) => {
    const result = reactive({
      isExternal: false,
      url: '',
      name: ''
    });
    watchEffect(() => {
      const val = linkRewriter(linkResolver.resolveLink(link(), lang ? lang : langResolver()));
      result.url = val === null || val === void 0 ? void 0 : val.url;
      result.name = val === null || val === void 0 ? void 0 : val.name;
      result.isExternal = val === null || val === void 0 ? void 0 : val.isExternal;
    });
    return result;
  });
  app.provide(configureResolveLinkRewriterKey, rewriter => {
    linkRewriter = rewriter;
  });
  app.provide(configureLanguageResolverKey, resolver => {
    langResolver = resolver;
  });
  setupEditor(app, options);
  app.component('blocks-repeater', script$1);
  app.component('block-container', script$2);
  app.component('rich-text', script);
  app.$registerRouterIgnoreQueryParams(/^_storyblok/);
}; // Create module definition for Vue.use()
/**
 * Shortcut for configuring a resolveLink rewriter. This needs to be called in a component's setup function.
 * @param rewriter
 */

function configureResolveLinkRewriter(rewriter) {
  const f = inject(configureResolveLinkRewriterKey, () => null);
  f(rewriter);
}
/**
 * Shortcut for configuring a language resolver. This needs to be called in a component's setup function.
 * @param resolver
 */

function configureLanguageResolver(resolver) {
  const f = inject(configureLanguageResolverKey, () => null);
  f(resolver);
}

export { LinkResolver, schema$1 as RichTextSchema, configureLanguageResolver, configureLanguageResolverKey, configureResolveLinkRewriter, configureResolveLinkRewriterKey, install as default, fetchDataSource, fetchDataSourceKey, fetchDataSourceReceiver, fetchPageReceiver, fetchStories, fetchStoriesReceiver, getDataSourceKey, getLinkForStory, getServerDataSourceKey, imageFit, imageResize, isInEditorKey, linkResolution, resolveLinkKey, serverRedirects, setPathResolver, setQueryParamsResolver, subscribeDataSourceKey, unsubscribeDataSourceKey, useDataSources };
